import { downloadFile } from "./fileUtils";
import { toPascalCase } from "./stringUtils";

// eslint-disable-next-line
const JSZip = require("jszip");

/**
 * Class responsible for generating and downloading an import template in ODS format.
 */
class ImportTemplateGenerator {
  /** @type {string} File name of the generated template */
  fileName;

  /** @type {Array} Headers of the generated spreadsheet */
  headers;

  /** @type {Array} Rows of the generated spreadsheet */
  rows;

  /** @type {Array} Constraints used for data validation in the spreadsheet */
  constraints;

  /** @type {number} Number of columns in the generated spreadsheet */
  numberOfColumns;

  /**
   * Constructor for ImportTemplateGenerator
   * @param {string} fileName - The file name of the template
   * @param {Object} options - Options for the template
   */
  constructor(fileName, options = {}) {
    this.fileName = fileName;

    this.headers = Array.isArray(options.headers) ? options.headers : [];
    this.rows = Array.isArray(options.rows) ? options.rows : [];
    this.constraints = Array.isArray(options.constraints) ? this._computeConstraints(options.constraints) : [];
    
    this.numberOfColumns = this.headers.length;
  }

  /**
   * Compares two contraints based on column index
   * @param {Object} constraintA - First constraint
   * @param {Object} constraintB - Second constraint
   * @returns {number} Comparison result
   */
  _compareConstraints = (constraintA, constraintB) => {
    return (
      this._getColumnIndexFromName(constraintA.columnName) -
      this._getColumnIndexFromName(constraintB.columnName)
    );
  };

  /**
   * Computes all data needed for constraints and sorts them by column index
   * @param {Array} constraints - List of constraints
   * @returns {Array} Processed constraints
   */
  _computeConstraints = (constraints) => {
    constraints.sort(this._compareConstraints);
    
    for (let i = 0; i < constraints.length; i++) {
      const constraint = constraints[i];

      if (constraint.type === "list") {
        constraint.sheetName = constraint.sheetName ? toPascalCase(constraint.sheetName) : `List${i + 1}`;
      }

      constraint.helpMessage = {
        title: constraint.helpMessage?.title ?? "Default help title",
        message: constraint.helpMessage?.message ?? "Default help description",
      };

      constraint.errorMessage = {
        title: constraint.errorMessage?.title ?? "Default error title",
        message: constraint.errorMessage?.message ?? "Default error description"
      };

      const previousConstraints = constraints.slice(0, i);
      const previousColumnsRepeatedSum = previousConstraints.reduce(
        (sum, constraint) => sum + constraint.columnsRepeated, 0
      );
      
      constraint.columnsRepeated =
        this._getColumnIndexFromName(constraint.columnName) -
        previousColumnsRepeatedSum -
        previousConstraints.length;
    }

    // Remove list constraints with no values
    return constraints.filter(constraint => constraint.type !== "list" || constraint.values.length > 0);
  };

  /**
   * Converts a column name (e.g., "A", "B", "AA") into its corresponding index
   * @param {string} columnName - Column name (Excel format)
   * @returns {number} Zero-based column index
   */
  _getColumnIndexFromName = (columnName) => {
    let index = 0;
    for (let i = 0; i < columnName.length; i++) {
      index = index * 26 + (columnName.charCodeAt(i) - "A".charCodeAt(0) + 1);
    }
    return index - 1;
  };

  /**
   * Converts a zero-based column index into its corresponding column name (e.g., "A", "B", "AA")
   * @param {number} columnIndex - Zero-based column index
   * @returns {string} Column name in Excel format
   */
  _getColumnNameFromIndex = (columnIndex) => {
    let columnName = "";
    columnIndex++; // Convert zero-based to one-based
    while (columnIndex > 0) {
      let remainder = (columnIndex - 1) % 26;
      columnName = String.fromCharCode("A".charCodeAt(0) + remainder) + columnName;
      columnIndex = Math.floor((columnIndex - 1) / 26);
    }
    return columnName;
  };

  /**
   * Generates the content XML for the ODS file
   * @returns {string} XML content
   */
  _getContentXMLFileContent = () => {
    return (
      `<?xml version="1.0" encoding="UTF-8"?>`+
      `<office:document-content xmlns:presentation="urn:oasis:names:tc:opendocument:xmlns:presentation:1.0" xmlns:css3t="http://www.w3.org/TR/css3-text/" xmlns:grddl="http://www.w3.org/2003/g/data-view#" xmlns:xhtml="http://www.w3.org/1999/xhtml" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns:xforms="http://www.w3.org/2002/xforms" xmlns:dom="http://www.w3.org/2001/xml-events" xmlns:script="urn:oasis:names:tc:opendocument:xmlns:script:1.0" xmlns:form="urn:oasis:names:tc:opendocument:xmlns:form:1.0" xmlns:math="http://www.w3.org/1998/Math/MathML" xmlns:number="urn:oasis:names:tc:opendocument:xmlns:datastyle:1.0" xmlns:field="urn:openoffice:names:experimental:ooo-ms-interop:xmlns:field:1.0" xmlns:meta="urn:oasis:names:tc:opendocument:xmlns:meta:1.0" xmlns:loext="urn:org:documentfoundation:names:experimental:office:xmlns:loext:1.0" xmlns:table="urn:oasis:names:tc:opendocument:xmlns:table:1.0" xmlns:chart="urn:oasis:names:tc:opendocument:xmlns:chart:1.0" xmlns:formx="urn:openoffice:names:experimental:ooxml-odf-interop:xmlns:form:1.0" xmlns:svg="urn:oasis:names:tc:opendocument:xmlns:svg-compatible:1.0" xmlns:rpt="http://openoffice.org/2005/report" xmlns:dr3d="urn:oasis:names:tc:opendocument:xmlns:dr3d:1.0" xmlns:tableooo="http://openoffice.org/2009/table" xmlns:draw="urn:oasis:names:tc:opendocument:xmlns:drawing:1.0" xmlns:of="urn:oasis:names:tc:opendocument:xmlns:of:1.2" xmlns:text="urn:oasis:names:tc:opendocument:xmlns:text:1.0" xmlns:style="urn:oasis:names:tc:opendocument:xmlns:style:1.0" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:calcext="urn:org:documentfoundation:names:experimental:calc:xmlns:calcext:1.0" xmlns:oooc="http://openoffice.org/2004/calc" xmlns:xlink="http://www.w3.org/1999/xlink" xmlns:drawooo="http://openoffice.org/2010/draw" xmlns:ooow="http://openoffice.org/2004/writer" xmlns:fo="urn:oasis:names:tc:opendocument:xmlns:xsl-fo-compatible:1.0" xmlns:ooo="http://openoffice.org/2004/office" xmlns:office="urn:oasis:names:tc:opendocument:xmlns:office:1.0" office:version="1.4">`+
        `<office:body>`+
          `<office:spreadsheet>`+
            this._getXMLValidations()+
            `<table:table table:name="Template">`+
              `<table:table-column table:number-columns-repeated="${this.numberOfColumns}" />`+
              this._getXMLHeaders()+
              this._getXMLRows()+
              this._getValidationTableRowsXML()+
            `</table:table>`+
            this._getValidationListTablesXML()+
          `</office:spreadsheet>`+
        `</office:body>`+
      `</office:document-content>`
    );
  }

  /**
   * Generates XML for table headers
   * @returns {string} XML content for table headers
   */
  _getXMLHeaders = () => {
    const cells = this.headers.map(header => {
      return `<table:table-cell office:value-type="string"><text:p>${header}</text:p></table:table-cell>`
    }).join("");

    return `<table:table-row>${cells}</table:table-row>`;
  }

  /**
   * Generates XML for table rows
   * @returns {string} XML content for table rows
   */
  _getXMLRows = () => {
    let rowsXML = "";
    for (let row of this.rows) {
      let rowXML = "";
      for (let i = 0; i < this.numberOfColumns; i++) {
        const cellValue = row[i];
        const columnName = this._getColumnNameFromIndex(i);

        // Handle empty cells
        if (cellValue === null || cellValue === undefined) {
          const cellConstraint = this.constraints.find(constraint => constraint.columnName === columnName);
          if (cellConstraint) {
            rowXML += `<table:table-cell table:content-validation-name="val${columnName}" />`;
            continue;
          }

          rowXML += `<table:table-cell />`;
          continue;
        }

        const cellType = typeof cellValue === "number" ? "float" : "string";

        // If the cell value is empty, we must not generate a full table cell
        if (cellType === "string" && cellValue.length === 0) {
          rowXML += `<table:table-cell />`;
          continue;
        }

        // Only add the content validation name if the constraint is defined for this column
        let contentValidationName = null;
        if (this.constraints.some(constraint => constraint.columnName === columnName)) {
          contentValidationName = `val${columnName}`;
        }

        rowXML += `<table:table-cell office:value-type="${cellType}" ${cellType !== "string" ? `office:value="${cellValue}"` : ""} ${contentValidationName ? `table:content-validation-name="${contentValidationName}"` : ""}>`.replace(/\s+/g, " ") // Ensures single space between attributes;
        rowXML += `<text:p>${cellValue}</text:p>`;
        rowXML += `</table:table-cell>`;
      }
      
      rowsXML += `<table:table-row>${rowXML}</table:table-row>`;
    }

    return rowsXML;
  }

  /**
   * Generates XML for table content validations
   * @returns {string} XML content for content validations
   */
  _getXMLValidations = () => {
    if (this.constraints.length === 0) {
      return "";
    }

    return (
      `<table:content-validations>`+
        this.constraints.map(constraint => this._getXMLValidation(constraint)).join("")+
      `</table:content-validations>`
    );
  }

  /**
   * Generates validation XML for a given constraint
   * @param {Object} constraint - The constraint object
   * @returns {string} XML content
   */
  _getXMLValidation = (constraint) => {
    const { columnName, values, sheetName, type, helpMessage, errorMessage } = constraint;

    let condition;
    if (type === "boolean") {
      condition = `of:cell-content-is-whole-number() and cell-content-is-between(0,1)`;
    }
    else if (type === "list" && Array.isArray(values)) {
      condition = `of:cell-content-is-in-list([$${sheetName}.$A$1:.$A$${values.length}])`;
    }

    return (
      `<table:content-validation table:name="val${columnName}" table:base-cell-address="Template.${columnName}2" table:allow-empty-cell="true" table:display-list="unsorted" table:condition="${condition}">`+
      `<table:help-message table:title="${helpMessage.title}" table:display="true">`+
          `<text:p>${helpMessage.message}</text:p>`+
        `</table:help-message>`+
        `<table:error-message table:message-type="stop" table:title="${errorMessage.title}" table:display="true">`+
          `<text:p>${errorMessage.message}</text:p>`+
        `</table:error-message>`+
      `</table:content-validation>`
    );
  }

  /**
   * Generates XML for validation table rows
   * @returns {string} XML content for validation table rows
   */
  _getValidationTableRowsXML = () => {
    if (this.constraints.length === 0) {
      return "";
    }

    return (
      `<table:table-row table:number-rows-repeated="9998">`+
        this.constraints.map(constraint => this._getValidationTableCellsXML(constraint)).join("")+
      `</table:table-row>`+
      `<table:table-row>`+
        this.constraints.map(constraint => this._getValidationTableCellsXML(constraint)).join("")+
      `</table:table-row>`
    );
  }

  /**
   * Generates XML for table cell validation
   * @param {Object} constraint - Contraint object
   * @returns {string} XML content for table cells
   */
  _getValidationTableCellsXML = (constraint) => {
    const { columnName, columnsRepeated } = constraint;
    return (
      (columnsRepeated !== 0 ? `<table:table-cell table:number-columns-repeated="${columnsRepeated}" />` : "") +
      `<table:table-cell table:content-validation-name="val${columnName}" />`
    );
  }

  /**
   * Generates XML for validation list tables
   * @returns {string} XML content for validation list tables
   */
  _getValidationListTablesXML = () => {
    const listContraints = this.constraints.filter(constraint => constraint.type === "list");
    if (listContraints.length === 0) {
      return "";
    }

    return listContraints.map(constraint => this._getValidationListTableXML(constraint)).join("");
  }

  /**
   * Generates XML for a validation list table
   * @param {Object} constraint - The constraint object
   * @returns {string} XML content for a validation list table
   */
  _getValidationListTableXML = (constraint) => {
    const { sheetName, values } = constraint;

    return (
      `<table:table table:name="${sheetName}" table:print="false">`+
        `<office:forms form:automatic-focus="false" form:apply-design-mode="false" />`+
        `<table:table-column />`+
        values.map((value) => {
          return (
            `<table:table-row>`+
              `<table:table-cell office:value-type="string">`+
                `<text:p>${value}</text:p>`+
              `</table:table-cell>`+
            `</table:table-row>`
          );
        }).join("")+
      `</table:table>`
    );
  }

  /**
   * Generates the manifest XML content for the ODS file
   * @returns {string} XML content
   */
  _getManifestXMLFileContent = () => {
    return (
      `<?xml version="1.0" encoding="UTF-8"?>`+
        `<manifest:manifest xmlns:manifest="urn:oasis:names:tc:opendocument:xmlns:manifest:1.0" manifest:version="1.4" xmlns:loext="urn:org:documentfoundation:names:experimental:office:xmlns:loext:1.0">`+
        `<manifest:file-entry manifest:full-path="/" manifest:version="1.4" manifest:media-type="application/vnd.oasis.opendocument.spreadsheet"/>`+
        `<manifest:file-entry manifest:full-path="manifest.rdf" manifest:media-type="application/rdf+xml"/>`+
        `<manifest:file-entry manifest:full-path="meta.xml" manifest:media-type="text/xml"/>`+
        `<manifest:file-entry manifest:full-path="styles.xml" manifest:media-type="text/xml"/>`+
        `<manifest:file-entry manifest:full-path="content.xml" manifest:media-type="text/xml"/>`+
      `</manifest:manifest>`
    );
  }

  /**
   * Generates RDF manifest content for the ODS file
   * @returns {string} XML content for RDF manifest
   */
  _getManifestRDFFileContent = () => {
    return (
      `<?xml version="1.0" encoding="utf-8"?>`+
      `<rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#">`+
        `<rdf:Description rdf:about="content.xml">`+
          `<rdf:type rdf:resource="http://docs.oasis-open.org/ns/office/1.2/meta/odf#ContentFile"/>`+
        `</rdf:Description>`+
        `<rdf:Description rdf:about="">`+
          `<ns0:hasPart xmlns:ns0="http://docs.oasis-open.org/ns/office/1.2/meta/pkg#" rdf:resource="content.xml"/>`+
        `</rdf:Description>`+
        `<rdf:Description rdf:about="styles.xml">`+
          `<rdf:type rdf:resource="http://docs.oasis-open.org/ns/office/1.2/meta/odf#StylesFile"/>`+
        `</rdf:Description>`+
        `<rdf:Description rdf:about="">`+
          `<ns0:hasPart xmlns:ns0="http://docs.oasis-open.org/ns/office/1.2/meta/pkg#" rdf:resource="styles.xml"/>`+
        `</rdf:Description>`+
        `<rdf:Description rdf:about="meta.xml">`+
          `<rdf:type rdf:resource="http://docs.oasis-open.org/ns/office/1.2/meta/odf#MetadataFile"/>`+
        `</rdf:Description>`+
        `<rdf:Description rdf:about="">`+
          `<ns0:hasPart xmlns:ns0="http://docs.oasis-open.org/ns/office/1.2/meta/pkg#" rdf:resource="meta.xml"/>`+
        `</rdf:Description>`+
        `<rdf:Description rdf:about="">`+
          `<rdf:type rdf:resource="http://docs.oasis-open.org/ns/office/1.2/meta/pkg#Document"/>`+
        `</rdf:Description>`+
      `</rdf:RDF>`
    );
  }

  /**
   * Returns the content of the meta XML file
   * @returns {string} Meta XML file content
   */
  _getMetaXMLFileContent = () => {
    return (
      `<?xml version="1.0" encoding="UTF-8"?>`+
      `<office:document-meta xmlns:grddl="http://www.w3.org/2003/g/data-view#"`+
      ` xmlns:meta="urn:oasis:names:tc:opendocument:xmlns:meta:1.0"`+
      ` xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:xlink="http://www.w3.org/1999/xlink"`+
      ` xmlns:ooo="http://openoffice.org/2004/office"`+
      ` xmlns:office="urn:oasis:names:tc:opendocument:xmlns:office:1.0" office:version="1.4">`+
        `<office:meta>`+
        `</office:meta>`+
      `</office:document-meta>`
    );
  }

  /**
   * Returns the content of the styles XML file
   * @returns {string} Styles XML file content
   */
  _getStylesXMLFileContent = () => {
    return (
      `<?xml version="1.0" encoding="UTF-8"?>`+
      `<office:document-styles xmlns:presentation="urn:oasis:names:tc:opendocument:xmlns:presentation:1.0"`+
      ` xmlns:css3t="http://www.w3.org/TR/css3-text/" xmlns:grddl="http://www.w3.org/2003/g/data-view#"`+
      ` xmlns:xhtml="http://www.w3.org/1999/xhtml" xmlns:dom="http://www.w3.org/2001/xml-events"`+
      ` xmlns:script="urn:oasis:names:tc:opendocument:xmlns:script:1.0"`+
      ` xmlns:form="urn:oasis:names:tc:opendocument:xmlns:form:1.0"`+
      ` xmlns:math="http://www.w3.org/1998/Math/MathML"`+
      ` xmlns:number="urn:oasis:names:tc:opendocument:xmlns:datastyle:1.0"`+
      ` xmlns:field="urn:openoffice:names:experimental:ooo-ms-interop:xmlns:field:1.0"`+
      ` xmlns:meta="urn:oasis:names:tc:opendocument:xmlns:meta:1.0"`+
      ` xmlns:loext="urn:org:documentfoundation:names:experimental:office:xmlns:loext:1.0"`+
      ` xmlns:table="urn:oasis:names:tc:opendocument:xmlns:table:1.0"`+
      ` xmlns:chart="urn:oasis:names:tc:opendocument:xmlns:chart:1.0"`+
      ` xmlns:svg="urn:oasis:names:tc:opendocument:xmlns:svg-compatible:1.0"`+
      ` xmlns:rpt="http://openoffice.org/2005/report"`+
      ` xmlns:dr3d="urn:oasis:names:tc:opendocument:xmlns:dr3d:1.0"`+
      ` xmlns:tableooo="http://openoffice.org/2009/table"`+
      ` xmlns:draw="urn:oasis:names:tc:opendocument:xmlns:drawing:1.0"`+
      ` xmlns:of="urn:oasis:names:tc:opendocument:xmlns:of:1.2"`+
      ` xmlns:text="urn:oasis:names:tc:opendocument:xmlns:text:1.0"`+
      ` xmlns:style="urn:oasis:names:tc:opendocument:xmlns:style:1.0"`+
      ` xmlns:dc="http://purl.org/dc/elements/1.1/"`+
      ` xmlns:calcext="urn:org:documentfoundation:names:experimental:calc:xmlns:calcext:1.0"`+
      ` xmlns:oooc="http://openoffice.org/2004/calc" xmlns:xlink="http://www.w3.org/1999/xlink"`+
      ` xmlns:drawooo="http://openoffice.org/2010/draw" xmlns:ooow="http://openoffice.org/2004/writer"`+
      ` xmlns:fo="urn:oasis:names:tc:opendocument:xmlns:xsl-fo-compatible:1.0"`+
      ` xmlns:ooo="http://openoffice.org/2004/office"`+
      ` xmlns:office="urn:oasis:names:tc:opendocument:xmlns:office:1.0" office:version="1.4">`+
      `</office:document-styles>`
    );
  }

  /**
   * Returns the MIME type for the generated ODS file
   * @returns {string} MIME type
   */
  _getMimeType = () => {
    return 'application/vnd.oasis.opendocument.spreadsheet';
  }

  /**
   * Creates a downloadable ODS file
   * @returns {Promise<string>} Object URL for the generated file
   */
  createTemplate = async () => {
    const zip = new JSZip();

    zip.file("META-INF/manifest.xml", this._getManifestXMLFileContent());
    zip.file("content.xml", this._getContentXMLFileContent());
    zip.file("manifest.rdf", this._getManifestRDFFileContent());
    zip.file("meta.xml", this._getMetaXMLFileContent());
    zip.file("styles.xml", this._getStylesXMLFileContent());
    zip.file("mimetype", this._getMimeType(), { compression: "STORE" });

    const blob = await zip.generateAsync({ type: "blob" });
    return URL.createObjectURL(blob);
  }

  /**
   * Initiates the download of the generated ODS template file
   */
  downloadTemplate = async () => {
    const url = await this.createTemplate();
    downloadFile(url, `${this.fileName}.ods`);
  }
}

export default ImportTemplateGenerator;